import React from "react";
import { useTranslation } from "react-i18next";
import ErrorMess from "../ui/ErrorMess";
import { MonthPicker } from "../ui/MonthPicker";
import { getEarliestValidStartDate } from "../../utils/dateUtils";
import { Partner, PartnerContact } from "../../types/addServiceTypes";
import type { InjectionFormData } from "../../pages/AddService";

// S5c — PURE REFACTOR (plan §5g/§5h). The step-3 "Injection Contract Parameters"
// form, lifted VERBATIM out of AddService.tsx (origin/main:2488-3032). Presentational
// only: it owns no state and performs no side effects — every value and callback is
// injected. Props deliberately reuse the ORIGINAL identifier names so the JSX body is
// byte-for-byte identical to the block it replaces, which is what makes this refactor
// provably inert.
//
// Extracted so the Customer Switch popup can render the same step with its own data
// (S5d) instead of duplicating the form — modelled on src/components/bessOnboarding/,
// which already uses this per-step-component shape.
//
// NOTE: the partner-selection cluster is intentionally NOT shared with
// OfftakeContractParameters. Both blocks were already duplicated in AddService, and
// sharing them would require threading label strings through purely to preserve the
// fact that the offtake form is untranslated (0 t() calls vs 48 here). Consolidating
// the three partner-selector copies (injection/offtake/combined) is a separate
// follow-up, as is the offtake form's missing i18n.

// Only the pslData fields this form reads. DataPSL["ean"] is structurally assignable.
interface InjectionContractParamsPsl {
  gsrn: string;
  apStreetName: string;
  apApartmentNumber: string;
  apZip: string;
  apCity: string;
  country: string;
  runningProcessLabel: string;
}

// The partner-selection cluster, grouped so the prop list stays readable. Destructured
// back to its original names below so the JSX body needs no edits.
export interface InjectionPartnerProps {
  partners: Partner[];
  partnerContacts: PartnerContact[];
  selectedPartnerLocal: Partner | null;
  partnerSearchTerm: string;
  setPartnerSearchTerm: (value: string) => void;
  showPartnerDropdown: boolean;
  setShowPartnerDropdown: (value: boolean) => void;
  handlePartnerChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePartnerContactChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  lastSelectedInjectionPartnerId: number | null;
  setLastSelectedInjectionPartnerId: (id: number | null) => void;
}

interface InjectionContractParametersProps {
  injectionFormData: InjectionFormData;
  handleInjectionFormChange: <K extends keyof InjectionFormData>(field: K, value: InjectionFormData[K]) => void;
  pslData: InjectionContractParamsPsl | null;
  selectedUserCompany?: { companyName?: string } | null;
  getMinEndDate: (start: string) => string;
  showVolumeConfirmation: boolean;
  isAdminOrPartner: boolean;
  user?: { user: { role: string } | null } | null;
  formValidationError: string | null;
  injectionError: string | null;
  offtakeError: string | null;
  partner: InjectionPartnerProps;
  // Modelled on bessOnboarding/ContractProp.tsx's StepNavProps.
  handleFormSubmit: (e?: React.FormEvent) => void;
  // Onboarding site create/link block, rendered beneath the read-only address card.
  siteSection?: React.ReactNode;
}

export const InjectionContractParameters: React.FC<InjectionContractParametersProps> = ({
  injectionFormData,
  handleInjectionFormChange,
  pslData,
  selectedUserCompany,
  getMinEndDate,
  showVolumeConfirmation,
  isAdminOrPartner,
  user,
  formValidationError,
  injectionError,
  offtakeError,
  partner,
  handleFormSubmit,
  siteSection,
}) => {
  // Same namespace AddService uses; all 44 distinct keys in this form are bare
  // (no `ns:` prefix), so resolution is identical to the inlined version.
  const { t } = useTranslation("addservice");

  const {
    partners,
    partnerContacts,
    selectedPartnerLocal,
    partnerSearchTerm,
    setPartnerSearchTerm,
    showPartnerDropdown,
    setShowPartnerDropdown,
    handlePartnerChange,
    handlePartnerContactChange,
    lastSelectedInjectionPartnerId,
    setLastSelectedInjectionPartnerId,
  } = partner;

  return (
                <form className="max-w-4xl mx-auto">
                  <h2 className="text-primary-600 mb-6 font-semibold text-2xl">
                    {t("services.injectionContractParameters")}
                  </h2>

                  {/* EAN Company Name and Address */}
                  {pslData && (
                    <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                      <div className="font-semibold text-primary-600 text-base mb-1">
                        {selectedUserCompany?.companyName}
                      </div>
                      <div className="text-primary-500 text-sm">
                        {pslData.apStreetName}
                        {pslData.apApartmentNumber && ` ${pslData.apApartmentNumber}`}, {pslData.apZip} {pslData.apCity}
                        , {pslData.country}
                      </div>
                      <div className="text-primary-400 text-xs mt-1">EAN: {pslData.gsrn}</div>
                    </div>
                  )}

                  {siteSection}

                  <div className="flex gap-6">
                    {/* Left Column */}
                    <div className="flex flex-1 flex-col gap-4">
                      {/* Contract Dates */}
                      <div className="flex gap-4">
                        <div className="flex flex-1 flex-col">
                          <label htmlFor="startDate" className="text-primary-600 mb-2 font-medium text-sm">
                            {t("contract.contractStartDate")} {t("misc.required")}
                          </label>
                          <MonthPicker
                            id="startDate"
                            value={injectionFormData.startDate}
                            onChange={(val) => handleInjectionFormChange("startDate", val)}
                            minDate={getEarliestValidStartDate()}
                            placeholder={t("contract.selectStartMonth")}
                            disabled={pslData?.runningProcessLabel?.toLowerCase().includes("under construction")}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {pslData?.runningProcessLabel?.toLowerCase().includes("under construction") ? (
                              <>
                                <span className="text-yellow-600 font-semibold">{t("status.underConstruction")}</span>
                                <br />
                                {t("contract.startDateDeterminedUponCompletion")}
                                <br />
                                {t("contact.salesForEstimatedDate")}
                              </>
                            ) : (
                              <>
                                {t("contract.startsFirstOfMonth")}
                                <br />
                                {t("contact.salesForEarlierStart")}
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex flex-1 flex-col">
                          <label htmlFor="endDate" className="text-primary-600 mb-2 font-medium text-sm">
                            {t("contract.contractEndDate")}
                          </label>

                          {/* Radio button options */}
                          <div className="space-y-2 mb-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="flexible-injection"
                                name="contract-end-injection"
                                checked={injectionFormData.isFlexible}
                                onChange={() => handleInjectionFormChange("isFlexible", true)}
                                className="accent-primary-600"
                              />
                              <label htmlFor="flexible-injection" className="text-primary-600 text-sm">
                                {t("contract.automaticMonthlyRenewal")}
                              </label>
                            </div>

                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="specific-end-injection"
                                name="contract-end-injection"
                                checked={!injectionFormData.isFlexible}
                                onChange={() => handleInjectionFormChange("isFlexible", false)}
                                className="accent-primary-600"
                              />
                              <label htmlFor="specific-end-injection" className="text-primary-600 text-sm">
                                {t("contract.specificEndDate")}
                              </label>
                            </div>
                          </div>

                          {/* Show date input only when specific end date is selected */}
                          {!injectionFormData.isFlexible && (
                            <input
                              type="date"
                              id="endDate"
                              value={injectionFormData.endDate}
                              onChange={(e) => handleInjectionFormChange("endDate", e.target.value)}
                              min={getMinEndDate(injectionFormData.startDate)}
                              className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 mb-1"
                            />
                          )}

                          <p className="text-xs text-gray-500">
                            {injectionFormData.isFlexible
                              ? t("contract.terminateAnyMonth")
                              : t("contract.mustBeAtLeast1MonthAfterStart")}
                          </p>
                        </div>
                      </div>

                      {/* Annual Injection Volume */}
                      <div className="flex flex-col">
                        <label htmlFor="annualInjection" className="text-primary-600 mb-2 font-medium text-sm">
                          {t("volume.annualInjectionVolume")} ({t("units.mwh")}) {t("misc.required")}
                        </label>

                        {/* Show input by default or when checkbox is unchecked */}
                        {!injectionFormData.annualVolumeUnknown ? (
                          <>
                            <input
                              type="number"
                              id="annualInjection"
                              placeholder={t("volume.volumePlaceholder")}
                              min="1"
                              value={injectionFormData.annualVolume || ""}
                              onChange={(e) => handleInjectionFormChange("annualVolume", Number(e.target.value))}
                              className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                              required={!injectionFormData.annualVolumeUnknown}
                            />
                            {showVolumeConfirmation && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2">
                                <div className="flex">
                                  <div className="flex-shrink-0">
                                    <svg
                                      className="h-5 w-5 text-yellow-400"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm text-yellow-700">{t("volume.confirmLargeVolume")}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          /* Show info message when unknown is checked */
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-sm text-gray-600">{t("volume.volumeEstimated")}</p>
                          </div>
                        )}

                        {/* Checkbox for unknown volume - now below the input */}
                        <div className="flex items-center gap-2 mt-3">
                          <input
                            type="checkbox"
                            id="unknown-volume"
                            checked={injectionFormData.annualVolumeUnknown}
                            onChange={(e) => handleInjectionFormChange("annualVolumeUnknown", e.target.checked)}
                            className="accent-primary-600"
                          />
                          <label htmlFor="unknown-volume" className="text-primary-600 text-sm">
                            {t("volume.iDontKnowVolume")}
                          </label>
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <label className="text-primary-600 mb-3 font-medium text-sm">
                          {t("partner.workingWithPartner")}
                        </label>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <label
                            className={`flex items-center gap-3 py-1 px-3 rounded-lg border-2 cursor-pointer transition-all
                            ${
                              injectionFormData.partnerId === null
                                ? "border-primary-100"
                                : "border-gray-100 hover:border-primary-100 bg-white"
                            }
                          `}
                          >
                            <input
                              type="radio"
                              name="workingWithPartnerInjection"
                              checked={injectionFormData.partnerId === 1}
                              onChange={() => {
                                handleInjectionFormChange("partnerId", 1);
                                handleInjectionFormChange("partnerContactId", null);
                              }}
                              className="accent-primary-600 w-4 h-4"
                            />
                            <div>
                              <span
                                className={`font-medium text-sm ${
                                  injectionFormData.partnerId === 1 ? "text-primary-700" : "text-primary-600"
                                }`}
                              >
                                {t("partner.standardYuso")}
                              </span>
                              <p className="text-xs text-primary-500 mt-0.5">{t("partner.directService")}</p>
                            </div>
                          </label>

                          <label
                            className={`
      flex items-center gap-3 py-1 px-3 rounded-lg border-2 cursor-pointer transition-all
      ${
        injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1
          ? "border-primary-100"
          : "border-gray-100 hover:border-primary-100 bg-white"
      }
    `}
                          >
                            <input
                              type="radio"
                              name="workingWithPartnerInjection"
                              checked={injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1}
                              onChange={() => {
                                if (injectionFormData.partnerId === null || injectionFormData.partnerId === 1) {
                                  // If we have a previously selected partner, use it; otherwise use -1
                                  const partnerIdToSet = lastSelectedInjectionPartnerId || -1;
                                  handleInjectionFormChange("partnerId", partnerIdToSet);

                                  // If we have a previously selected partner, also restore its name in the search
                                  if (lastSelectedInjectionPartnerId && lastSelectedInjectionPartnerId > 0) {
                                    const partner = partners.find((p) => p.id === lastSelectedInjectionPartnerId);
                                    if (partner) {
                                      setPartnerSearchTerm(partner.name);
                                    }
                                  }
                                }
                              }}
                              className="accent-primary-600 w-4 h-4"
                            />
                            <div>
                              <span
                                className={`font-medium text-sm ${
                                  injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1
                                    ? "text-primary-700"
                                    : "text-primary-600"
                                }`}
                              >
                                {t("partner.partnerService")}
                              </span>
                              <p className="text-xs text-primary-500 mt-0.5">{t("partner.viaPartner")}</p>
                            </div>
                          </label>
                        </div>

                        {/* Partner selection - animated appearance */}
                        {injectionFormData.partnerId !== null && injectionFormData.partnerId !== 1 && (
                          <div className="border border-gray-100 rounded-lg p-4 space-y-4 animate-fadeIn">
                            <div>
                              <label
                                htmlFor="partnerSearchInjection"
                                className="text-primary-600 mb-2 font-medium text-xs uppercase tracking-wide block"
                              >
                                {t("partner.selectPartner")}
                              </label>

                              <div className="relative">
                                {/* Search input */}
                                <div className="relative">
                                  <input
                                    id="partnerSearchInjection"
                                    type="text"
                                    placeholder={t("partner.searchPartner")}
                                    value={partnerSearchTerm}
                                    onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                    onFocus={() => setShowPartnerDropdown(true)}
                                    className="w-full border-2 rounded-md px-3 py-2.5 pr-10 border-gray-100 bg-white text-primary-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                  />
                                  <svg
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                  </svg>
                                </div>

                                {/* Dropdown with filtered partners */}
                                {showPartnerDropdown && (
                                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {partners
                                      .filter((partner) =>
                                        partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                      )
                                      .slice(0, 10) // Limit to 10 results for better UX
                                      .map((partner) => (
                                        <button
                                          key={partner.id}
                                          type="button"
                                          onClick={() => {
                                            handleInjectionFormChange("partnerId", partner.id);
                                            setLastSelectedInjectionPartnerId(partner.id); // Save the selected partner

                                            // Create a proper synthetic event
                                            const syntheticEvent = {
                                              target: { value: partner.id.toString() },
                                            } as React.ChangeEvent<HTMLSelectElement>;
                                            handlePartnerChange(syntheticEvent);
                                            setPartnerSearchTerm(partner.name);
                                            setShowPartnerDropdown(false);
                                          }}
                                          className="w-full text-left px-3 py-2 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors text-sm"
                                        >
                                          <div className="font-medium text-primary-700">{partner.name}</div>
                                          {partner.vatNumber && (
                                            <div className="text-xs text-gray-500">VAT: {partner.vatNumber}</div>
                                          )}
                                        </button>
                                      ))}

                                    {/* No results message */}
                                    {partners.filter((partner) =>
                                      partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                    ).length === 0 && (
                                      <div className="px-3 py-2 text-sm text-gray-500">
                                        {t("partner.noPartnersFound")} "{partnerSearchTerm}"
                                      </div>
                                    )}

                                    {/* Show more message if there are more results */}
                                    {partners.filter((partner) =>
                                      partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
                                    ).length > 10 && (
                                      <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                        {t("partner.showingFirstResults")}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Selected partner display */}
                              {injectionFormData.partnerId && injectionFormData.partnerId > 0 && (
                                <div className="mt-2 p-2 bg-primary-50 rounded-md">
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm">
                                      <span className="text-gray-600">{t("partner.selected")}:</span>
                                      <span className="ml-2 font-medium text-primary-700">
                                        {partners.find((p) => p.id === injectionFormData.partnerId)?.name}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPartnerSearchTerm("");
                                        handleInjectionFormChange("partnerId", -1);
                                      }}
                                      className="text-xs text-primary-600 hover:text-primary-700 underline"
                                    >
                                      {t("partner.change")}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Helper text for partner selection */}
                            <div className="flex items-start gap-2 pt-2">
                              <svg
                                className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0118 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <p className="text-xs text-primary-600">{t("partner.partnerWillManage")}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column (Admin/Partner only) */}
                    {isAdminOrPartner && (
                      <div className="flex flex-1 flex-col gap-4">
                        {/* Platform Fee */}
                        <div className="flex flex-col">
                          <label htmlFor="balancingFeeInjection" className="text-primary-600 mb-2 font-medium text-sm">
                            {t("fees.platformFee")} ({t("units.eurPerMwh")})
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              id="balancingFeeInjection"
                              placeholder="e.g. 8"
                              value={injectionFormData.platformFee || ""}
                              onChange={(e) =>
                                handleInjectionFormChange("platformFee", e.target.value ? Number(e.target.value) : null)
                              }
                              className="flex-1 border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                            />
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                              {t("units.eurPerMwh")}
                            </span>
                          </div>
                          {user?.user?.role === "partner" && (
                            <p className="text-xs text-gray-500 mt-1">{t("fees.onlyIncreaseFee")}</p>
                          )}
                        </div>

                        {/* Partner Information */}
                        <div className="flex flex-col">
                          <label htmlFor="partner" className="text-primary-600 mb-2 font-medium text-sm">
                            {t("partner.partner")}
                          </label>
                          <select
                            id="partner"
                            value={injectionFormData.partnerId || ""}
                            onChange={(e) => {
                              handleInjectionFormChange("partnerId", e.target.value ? Number(e.target.value) : null);
                              handlePartnerChange(e);
                            }}
                            disabled={user?.user?.role !== "admin"}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">{t("partner.selectAPartner")}</option>
                            {partners.map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Partner Contact */}
                        <div className="flex flex-col">
                          <label
                            htmlFor="partnerContactInjection"
                            className="text-primary-600 mb-2 font-medium text-sm"
                          >
                            {t("partner.partnerContact")}
                          </label>
                          <select
                            id="partnerContactInjection"
                            value={injectionFormData.partnerContactId || ""}
                            onChange={(e) => {
                              handleInjectionFormChange(
                                "partnerContactId",
                                e.target.value ? Number(e.target.value) : null
                              );
                              handlePartnerContactChange(e);
                            }}
                            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
                          >
                            <option value="">{t("partner.selectAContact")}</option>
                            {partnerContacts.map((contact) => (
                              <option key={contact.id} value={contact.id}>
                                {contact.firstName} {contact.lastName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Partner Fee */}
                        <div className="flex flex-col">
                          <label htmlFor="partnerFeeInjection" className="text-primary-600 mb-2 font-medium text-sm">
                            {t("fees.partnerFee")} ({t("units.eurPerMwh")})
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              id="partnerFeeInjection"
                              placeholder={t("fees.calculatedAutomatically")}
                              value={selectedPartnerLocal?.defaultPartnerFee ?? ""}
                              readOnly
                              className="flex-1 border rounded-md px-3 py-2 border-gray-200 bg-gray-50 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20 rounded-r-none"
                            />
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-200 rounded-r text-xs text-primary-400">
                              {t("units.eurPerMwh")}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t("fees.calculatedAs")}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="confirm-values-injection"
                        checked={injectionFormData.valuesConfirmed}
                        onChange={(e) => handleInjectionFormChange("valuesConfirmed", e.target.checked)}
                        className="accent-primary-600 w-5 h-5 mt-0.5"
                        required
                      />
                      <label htmlFor="confirm-values-injection" className="text-primary-600 text-sm">
                        <span className="font-medium">{t("validation.confirmValues")}</span>
                        <p className="text-xs text-gray-500 mt-1">{t("validation.confirmValuesDescription")}</p>
                      </label>
                    </div>
                  </div>

                  {/* Error Messages */}
                  <div className="mt-4">
                    {formValidationError && <ErrorMess error={formValidationError} />}
                    {injectionError && <ErrorMess error={injectionError} />}
                    {offtakeError && <ErrorMess error={offtakeError} />}
                  </div>
                  {/* Submit Button */}
                  <button
                    onClick={handleFormSubmit}
                    disabled={!injectionFormData.valuesConfirmed}
                    className={`py-3 px-6 mt-6 rounded-md text-white text-sm font-semibold transition-colors duration-200 ${
                      injectionFormData.valuesConfirmed
                        ? "bg-primary-600 hover:bg-primary-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {t("form.continueToSummary")}
                  </button>
                </form>
  );
};
